/**
 * Collision-resistant identifiers (docs/09). `crypto.randomUUID` is only available in
 * secure contexts, so a UUID v4 is built from `crypto.getRandomValues`, which also
 * works when a tablet opens the development server over plain LAN HTTP.
 */
export type IdKind =
  'project' | 'design' | 'node' | 'nail' | 'snapshot' | 'collection' | 'template';

export type IdGenerator = (kind: IdKind) => string;

export function uuidV4(): string {
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Default generator: `<kind>-<uuid>` matches the contract ID pattern. */
export const randomIds: IdGenerator = (kind) => `${kind}-${uuidV4()}`;

/** Deterministic generator for tests and reproducible fixtures. */
export function sequentialIds(prefix = 'test'): IdGenerator {
  const counters = new Map<IdKind, number>();
  return (kind) => {
    const next = (counters.get(kind) ?? 0) + 1;
    counters.set(kind, next);
    return `${prefix}-${kind}-${next}`;
  };
}
